import { useCallback, useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { UnsubscribedNotice, UnsubscribeFlow } from "@/components/dev/UnsubscribeFlow";

const RefList = ({ p, testId }) => (
  <ul className="max-h-40 min-w-0 space-y-0.5 overflow-y-auto text-xs text-neutral-200" data-testid={testId}>
    {p.refs.map((r) => <li key={r} className="[overflow-wrap:anywhere]"><b>{r}</b>{p.names[r] ? ` · ${p.names[r]}` : ""}</li>)}
  </ul>
);

// Client space: his per-business (REF) plans; a recurring one can be cancelled on its own (same 4-step flow), all its REFs together.
export const BusinessPlansCard = () => {
  const { t, fmtDate } = useI18n();
  const [plans, setPlans] = useState([]);
  const load = useCallback(() => api.get("/dev/billing/business-plans").then(({ data }) => setPlans(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!plans.length) return null;
  return (
    <section className="card-carbon min-w-0 space-y-4 rounded-2xl p-5" data-testid="client-business-plans">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Building2 className="h-4 w-4 shrink-0" /> {t("biz.plansTitle")}</h2>
      {plans.map((p) => (
        <div key={p.id} className="min-w-0 space-y-2 rounded-xl border border-emerald-500/30 p-3" data-testid={`client-plan-${p.id}`}>
          <p className="break-words text-sm font-semibold text-white">{t("biz.planRow", { app: p.app, n: p.refs.length, status: t(`biz.st.${p.status}`), until: p.until ? fmtDate(p.until) : "—", emp: p.employees })}</p>
          <RefList p={p} testId={`client-plan-refs-${p.id}`} />
          {p.cancellable && (p.cancel_at_period_end
            ? <UnsubscribedNotice billing={{ period_end: p.until }} onChange={load} url={`/dev/billing/business-plans/${p.id}/resubscribe`} testId={`client-plan-unsubscribed-${p.id}`} />
            : (
              <UnsubscribeFlow billing={{ period_end: p.until }} onChange={load} url={`/dev/billing/business-plans/${p.id}/unsubscribe`} testId={`client-plan-unsubscribe-${p.id}`}>
                <div className="min-w-0 space-y-1 rounded-lg border border-red-500/40 bg-red-950/30 p-2" data-testid={`unsub-plan-refs-${p.id}`}>
                  <p className="break-words text-xs font-semibold text-red-100">{t("biz.unsubList", { app: p.app, n: p.refs.length })}</p>
                  <RefList p={p} testId={`unsub-plan-reflist-${p.id}`} />
                </div>
              </UnsubscribeFlow>
            ))}
        </div>
      ))}
    </section>
  );
};
